import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CorteCajaService } from '../services/corte-caja.service';
import { AuthService } from '../services/auth.service';
import { CorteCajaDialogComponent } from './corte-caja-dialog/corte-caja-dialog.component';
import { take } from 'rxjs/operators';

const MY_DATE_FORMATS = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-corte-caja',
  templateUrl: './corte-caja.component.html',
  styleUrls: ['./corte-caja.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ]
})
export class CorteCajaComponent implements OnInit {
  filtroForm: FormGroup;
  cortes: any[] = [];
  isLoading = false;
  isAdministrador = false;

  displayedColumns = ['fecha', 'usuario', 'ventas', 'efectivoSistema', 'efectivoContado', 'diferencia', 'estado', 'acciones'];

  constructor(
    private fb: FormBuilder,
    private corteCajaService: CorteCajaService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.filtroForm = this.fb.group({
      startDate: [moment().startOf('month').toDate()],
      endDate: [moment().endOf('month').toDate()]
    });
    this.checkUserRole();
    this.cargarCortes();
  }

  private checkUserRole(): void {
    const currentUser = this.authService.getCurrentUserSnapshot();
    if (currentUser?.['rol']) {
      const rolNombre = typeof currentUser['rol'] === 'string'
        ? currentUser['rol']
        : currentUser['rol']?.['rol'];
      this.isAdministrador = rolNombre === 'Administrador';
    } else {
      this.authService.currentUser$.pipe(take(1)).subscribe(user => {
        if (user?.['rol']) {
          const rolNombre = typeof user['rol'] === 'string'
            ? user['rol']
            : user['rol']?.['rol'];
          this.isAdministrador = rolNombre === 'Administrador';
        }
      });
    }
  }

  cargarCortes(): void {
    const { startDate, endDate } = this.filtroForm.value;
    if (!startDate || !endDate) return;

    this.isLoading = true;
    const start = moment(startDate).startOf('day').toISOString();
    const end = moment(endDate).endOf('day').toISOString();

    this.corteCajaService.getCortes({ fechaInicio: start, fechaFin: end }).subscribe({
      next: (data) => {
        this.cortes = Array.isArray(data) ? data : (data?.data ?? []);
        this.isLoading = false;
      },
      error: () => {
        this.toastr.error('Error al cargar los cortes de caja');
        this.isLoading = false;
      }
    });
  }

  abrirNuevoCorte(): void {
    this.dialog.open(CorteCajaDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { modo: 'crear' }
    }).afterClosed().subscribe((success: boolean) => {
      if (success) this.cargarCortes();
    });
  }

  abrirCorteDia(): void {
    const currentUser = this.authService.getCurrentUserSnapshot();
    const usuarioId = currentUser?.['userId'] ?? currentUser?.['id'];
    if (!usuarioId) {
      this.toastr.error('No se pudo obtener el usuario actual');
      return;
    }
    this.isLoading = true;
    this.corteCajaService.crearCorteDia({ usuarioId }).subscribe({
      next: () => {
        this.toastr.success('Corte de hoy creado exitosamente');
        this.cargarCortes();
      },
      error: (err) => {
        const msg = err?.error?.message || 'Error al crear el corte';
        this.toastr.error(msg);
        this.isLoading = false;
      }
    });
  }

  verDetalle(corte: any): void {
    this.dialog.open(CorteCajaDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      data: { modo: 'ver', corte }
    });
  }

  cerrarCorte(corte: any): void {
    this.dialog.open(CorteCajaDialogComponent, {
      width: '500px',
      maxWidth: '95vw',
      data: { modo: 'cerrar', corte }
    }).afterClosed().subscribe((success: boolean) => {
      if (success) this.cargarCortes();
    });
  }

  get totalVentasPeriodo(): number {
    return this.cortes.reduce((sum, c) => sum + (Number(c.totalVentas) || 0), 0);
  }

  get totalEfectivoPeriodo(): number {
    return this.cortes.reduce((sum, c) => sum + (Number(c.totalEfectivo) || 0), 0);
  }

  get diferenciaPeriodo(): number {
    return this.cortes
      .filter(c => c.estado === 'cerrado' && c.diferencia != null)
      .reduce((sum, c) => sum + (Number(c.diferencia) || 0), 0);
  }

  exportarPdf(): void {
    if (!this.cortes.length) {
      this.toastr.warning('No hay datos para exportar');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const { startDate, endDate } = this.filtroForm.value;
    const title = `Cortes de Caja — ${moment(startDate).format('DD/MM/YYYY')} al ${moment(endDate).format('DD/MM/YYYY')}`;
    doc.setFontSize(14);
    doc.text(title, 40, 40);

    autoTable(doc, {
      startY: 60,
      head: [['Fecha Inicio', 'Fecha Fin', 'Usuario', 'Ventas', 'Efectivo Sis.', 'Tarjeta', 'Transferencia', 'Efectivo Contado', 'Diferencia', 'Estado']],
      body: this.cortes.map(c => [
        moment(c.fechaInicio).format('DD/MM/YYYY HH:mm'),
        moment(c.fechaFin).format('DD/MM/YYYY HH:mm'),
        c.usuario?.name || c.usuario?.email || '—',
        `$${Number(c.totalVentas).toFixed(2)}`,
        `$${Number(c.totalEfectivo).toFixed(2)}`,
        `$${Number(c.totalTarjeta).toFixed(2)}`,
        `$${Number(c.totalTransferencia).toFixed(2)}`,
        c.efectivoContado != null ? `$${Number(c.efectivoContado).toFixed(2)}` : '—',
        c.diferencia != null ? `$${Number(c.diferencia).toFixed(2)}` : '—',
        c.estado === 'cerrado' ? 'Cerrado' : 'Abierto'
      ]),
      theme: 'grid',
      headStyles: { fillColor: [63, 81, 181], textColor: 255 },
    });

    doc.save(`corte-caja-${moment().format('YYYYMMDD')}.pdf`);
    this.toastr.success('PDF generado');
  }
}
